import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { type AuthUser, type LoginDto, type RegisterDto } from '@pokefolio/types';

import { authService } from '../services/auth.service';

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  login: (data: LoginDto) => Promise<void>;
  register: (data: RegisterDto) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: AuthUser | null) => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Essayer de récupérer l'utilisateur (le cookie httpOnly sera envoyé automatiquement)
        const userData = await authService.getMe();
        setUser(userData);
      } catch (error) {
        // Si erreur 401, l'utilisateur n'est pas authentifié (cookie expiré ou absent)
        console.error('Failed to fetch user:', error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth().catch(console.error);
  }, []);

  const login = async (data: LoginDto) => {
    const response = await authService.login(data);
    setUser(response.user);
  };

  const register = async (data: RegisterDto) => {
    await authService.register(data);
    // Note: L'inscription ne connecte pas l'utilisateur immédiatement (vérification email requise)
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch {
      // Session already revoked or expired: signing out locally is all that is left.
    } finally {
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        setUser,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
